// Validação local dos formulários de pessoas (espelha as regras do servidor; o servidor continua a autoridade — Parte 14.1):
// formatos de UUID e data civil, CPF e CNPJ por módulo 11 (REQ-PPL-023: o CPF só vale para `Country=BR`). Puro, sem React.

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const COUNTRY_PATTERN = /^[A-Za-z]{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Identificador fiscal de país que não é o Brasil: formato livre, sem dígito verificador, até 32 caracteres (ADR-024). */
export const FEDERAL_CODE_FREEFORM_MAX = 32;

/** Erro de campo: caminho do campo no formulário e a chave de mensagem (`data.validation.<chave>`). */
export interface FieldIssue {
  readonly path: readonly string[];
  readonly message: string;
}

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

export function isEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

/** Data civil `yyyy-MM-dd` que existe no calendário. */
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** Data de hoje (`yyyy-MM-dd`, UTC) — só para limites de formulário; o servidor valida de novo. */
export function todayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Soma dias a uma data civil (`yyyy-MM-dd`). */
export function addDaysIso(value: string, days: number): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days))
    .toISOString()
    .slice(0, 10);
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** CPF por módulo 11 (11 dígitos, não repetidos). Aceita máscara. */
export function isValidCpf(value: string): boolean {
  const digits = digitsOnly(value);
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {
    return false;
  }
  const numbers = Array.from(digits, Number);
  const check = (length: number): number => {
    const sum = numbers
      .slice(0, length)
      .reduce((total, digit, index) => total + digit * (length + 1 - index), 0);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === numbers[9] && check(10) === numbers[10];
}

const CNPJ_WEIGHTS_12 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const CNPJ_WEIGHTS_13 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** CNPJ por módulo 11 (14 dígitos, não repetidos). Aceita máscara. */
export function isValidCnpj(value: string): boolean {
  const digits = digitsOnly(value);
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) {
    return false;
  }
  const numbers = Array.from(digits, Number);
  const check = (weights: readonly number[]): number => {
    const sum = weights.reduce((total, weight, index) => total + weight * (numbers[index] ?? 0), 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return check(CNPJ_WEIGHTS_12) === numbers[12] && check(CNPJ_WEIGHTS_13) === numbers[13];
}

/** Texto aparado ou `null` quando vazio. */
export function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
